// One dispatch per unit of work, carrying the state and every result the unit produced. There is
// no equality suppression: a unit whose events all folded to nothing still fires with an empty
// list so a subscriber tracking sync progress sees it. Fire-on-subscribe delivers the current
// state with no results.
//
// Reentrancy: a listener that triggers another dispatch during its own has that dispatch queued
// until the current one finishes, so every listener sees every unit in order.

export type Listener<TState, TResult> = (state: TState, results: readonly TResult[]) => void;
export type Unsubscribe = () => void;

const PENDING_MAX = 10_000;
const NO_RESULTS: readonly never[] = [];

function safeInvoke<TState, TResult>(listener: Listener<TState, TResult>, state: TState, results: readonly TResult[]): void {
  try { listener(state, results); }
  catch (e) {
    const report = (globalThis as { reportError?: (e: unknown) => void }).reportError;
    if (report !== undefined) report(e);
    else console.error(e);
  }
}

export class Notifier<TState, TResult = unknown> {
  private readonly listeners = new Set<Listener<TState, TResult>>();
  private current: TState;
  private dispatching = false;
  private readonly pending: Array<{ state: TState; results: readonly TResult[] }> = [];

  constructor(initial: TState) {
    this.current = initial;
  }

  get value(): TState {
    return this.current;
  }

  // Replaces the value without a dispatch; for open() and purge, where nobody is listening yet
  // or the replica is gone.
  reset(state: TState): void {
    this.current = state;
  }

  set(next: TState, results: readonly TResult[]): void {
    if (this.dispatching) {
      this.pending.push({ state: next, results });
      if (this.pending.length > PENDING_MAX) throw new Error(`Notifier: ${PENDING_MAX} dispatches queued from inside listeners`);
      return;
    }
    this.dispatch(next, results);
  }

  subscribe(listener: Listener<TState, TResult>): Unsubscribe {
    let alive = true;
    this.listeners.add(listener);
    queueMicrotask(() => {
      if (!alive) return;
      safeInvoke(listener, this.current, NO_RESULTS);
    });
    return () => {
      alive = false;
      this.listeners.delete(listener);
    };
  }

  private dispatch(next: TState, results: readonly TResult[]): void {
    this.dispatching = true;
    try {
      this.current = next;
      // Snapshot per frame so subscribe/unsubscribe during dispatch neither skips nor repeats.
      for (const listener of Array.from(this.listeners)) safeInvoke(listener, this.current, results);
      let drained = 0;
      while (this.pending.length > 0) {
        drained += 1;
        const queued = this.pending.shift()!;
        this.current = queued.state;
        for (const listener of Array.from(this.listeners)) safeInvoke(listener, this.current, queued.results);
      }
      if (drained > PENDING_MAX) throw new Error('Notifier: reentrant dispatch did not settle');
    } finally {
      this.dispatching = false;
    }
  }
}
